import { merge } from "lodash-es";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { createBreakoutSessionSlice } from "@/features/breakout-sessions/utils/breakout-session-slice";
import { createCallSettingsSlice } from "@/features/call-settings/utils/call-settings-slice";
import { createChatSlice } from "@/features/chat/utils/chat-slice";
import { createClosedCaptioningSlice } from "@/features/closed-captioning/utils/closed-captioning-slice";
import { createParticipantsSlice } from "@/features/participants/utils/participants-slice";
import { createVidyoDeviceSlice } from "@/features/vidyo-devices/utils/vidyo-device-slice";
import { createScreenSharingSlice } from "@/features/vidyo-screen-share/utils/screen-sharing-slice";
import sentryMiddleware from "@/utils/zustand-sentry.middleware";

import { connectToVidyo } from "../api/connect-to-vidyo";
import {
  SliceCreator,
  VidyoCommonState,
  VidyoStoreStateCreator,
} from "../types/vidyo-common";
import { VidyoConnector } from "../types/vidyoclient-nativewebrtc-sdk";

export type VidyoSdkState = {
  vidyoSDK: VidyoConnector | undefined;
  isVidyoReady: boolean;
  isVidyoInilizationStarted: boolean;
  initializeVidyo: () => Promise<void>;
};

export const createVidyoSdkSlice: SliceCreator<keyof VidyoSdkState> = (
  set,
  get,
) => ({
  vidyoSDK: undefined,
  isVidyoReady: false,
  isVidyoInilizationStarted: false,
  initializeVidyo: async () => {
    const state = get();
    // dont init more than once
    if (state.isVidyoReady || state.isVidyoInilizationStarted) {
      return;
    }

    set({ isVidyoInilizationStarted: true }, undefined, "vidyo/initializing");

    const vidyoConnector = await connectToVidyo();

    set(
      { vidyoSDK: vidyoConnector, isVidyoReady: true },
      undefined,
      "vidyo/initialized",
    );
  },
});

export const vidyoStoreCreator: VidyoStoreStateCreator = (...a) => ({
  ...createVidyoDeviceSlice(...a),
  ...createParticipantsSlice(...a),
  ...createVidyoSdkSlice(...a),
  ...createCallSettingsSlice(...a),
  ...createScreenSharingSlice(...a),
  ...createChatSlice(...a),
  ...createBreakoutSessionSlice(...a),
  ...createClosedCaptioningSlice(...a),
});

const stateTransformer = (state: VidyoCommonState) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { vidyoSDK, ...rest } = state;
  const cleanedState = {
    ...rest,
    chat: {
      ...state.chat,
      //dont log actual messages
      messages: state.chat.messages.length,
    },
  };
  // In zustand, actions are accessible from the store's state
  // We might want to remove them before sending the state to Sentry
  const filterNonFunctions = (
    obj: Record<string, unknown>,
  ): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]) => {
          if (typeof value === "object" && value !== null) {
            return [key, filterNonFunctions(value as Record<string, unknown>)];
          }
          return typeof value !== "function" ? [key, value] : [];
        })
        .filter((entry) => entry.length > 0),
    );
  };

  return filterNonFunctions(cleanedState);
};

export const useVidyoStore = create<VidyoCommonState>()(
  devtools(
    sentryMiddleware(
      persist(immer(vidyoStoreCreator), {
        name: "classview-vidyo-settings",
        merge: (persistedState, currentState) =>
          merge(currentState, persistedState),
        partialize: (state: VidyoCommonState) => ({
          camera: {
            preferred: state.camera.preferred,
          },
          microphone: {
            preferred: state.microphone.preferred,
          },
          speaker: {
            preferred: state.speaker.preferred,
          },
          displayName: state.displayName,
        }),
      }),
      {
        stateTransformer,
      },
    ),
  ),
);
